import { browserAccountStateSessionRepository } from "./account-state-session-repository.js";
import {
  canonicalizeTutorialProgressCommandIds,
  decodeTutorialProgressEnabledState,
} from "../tutorial-progress-contract.js";
import {
  createTutorialProgressAccountProvider,
} from "./tutorial-progress-account-provider.js";
import {
  browserLocalTutorialProgressRepository,
} from "../tutorial-progress-repository.js";

const CONTRACT_VERSION = 1;
const MAX_REVISION_LENGTH = 128;
const BROWSER_REPOSITORY_PROPERTY = "__fishystuffTutorialProgressRepository";
const BROWSER_REPOSITORY_BRAND = Symbol.for(
  "fishystuff.browser-tutorial-progress-repository.v1",
);

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function validateOptions(options) {
  if (options == null) return undefined;
  if (!isRecord(options)) {
    throw new TypeError("Repository command options must be an object.");
  }
  const fields = Object.keys(options);
  if (fields.length > 1 || fields.some((field) => field !== "expectedRevision")) {
    throw new TypeError("Repository command options contain unsupported fields.");
  }
  const revision = options.expectedRevision;
  if (
    revision !== undefined
    && revision !== null
    && (
      typeof revision !== "string"
      || revision.length === 0
      || revision.length > MAX_REVISION_LENGTH
    )
  ) {
    throw new TypeError("Expected revision must be null or a bounded non-empty string.");
  }
  return revision;
}

function freezeDomainState(value) {
  if (value.kind === "ready" || value.kind === "conflict") {
    return Object.freeze({
      ...value,
      completedIds: Object.freeze(value.completedIds.slice()),
    });
  }
  return Object.freeze({ ...value });
}

function domainStateFromAccount(projection) {
  if (projection.state.kind === "missing") {
    return freezeDomainState({ kind: "missing" });
  }
  return freezeDomainState({
    kind: "ready",
    revision: projection.revision,
    completedIds: projection.state.completedIds,
  });
}

function domainConflict(projection, expectedRevision) {
  return freezeDomainState({
    kind: "conflict",
    expectedRevision,
    actualRevision: projection.state.kind === "ready"
      ? projection.revision
      : null,
    completedIds: projection.state.kind === "ready"
      ? projection.state.completedIds
      : [],
  });
}

function freezeSyncState(value) {
  if (value.kind === "enabled") {
    return Object.freeze({
      kind: "enabled",
      revision: value.revision,
      state: value.state,
    });
  }
  if (value.kind === "conflict") {
    return Object.freeze({
      kind: "conflict",
      authoritativeState: value.authoritativeState,
    });
  }
  return Object.freeze({ ...value });
}

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

export function createTutorialProgressSyncRepository({
  localRepository,
  sessionRepository,
  accountProvider = null,
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  createOperationId,
} = {}) {
  if (
    !hasMethods(
      localRepository,
      ["exportRaw", "load", "state", "complete", "reset", "subscribe"],
    )
    || !hasMethods(sessionRepository, ["load", "refresh", "state", "subscribe"])
  ) {
    throw new TypeError("Tutorial progress sync requires its typed repositories.");
  }
  const remote = accountProvider ?? createTutorialProgressAccountProvider({
    apiBaseUrl,
    fetchImpl,
    ...(createOperationId === undefined ? {} : { createOperationId }),
  });
  if (!hasMethods(remote, ["load", "enable", "complete", "reset"])) {
    throw new TypeError("Tutorial progress sync requires its account provider.");
  }

  let currentState = freezeDomainState({ kind: "loading" });
  let currentSyncState = freezeSyncState({ kind: "loading" });
  let accountProjection = null;
  let accountId = null;
  let authority = "unknown";
  let requestId = 0;
  let requestController = null;
  let sessionLoadDepth = 0;
  let disposed = false;
  const listeners = new Set();
  const syncListeners = new Set();

  function state() {
    return freezeDomainState(currentState);
  }

  function syncState() {
    return currentSyncState;
  }

  function deliver(listener, value) {
    try {
      listener(value);
    } catch (_error) {
      globalThis.reportError?.(new Error("Tutorial progress subscriber failed."));
    }
  }

  function publish(value) {
    currentState = freezeDomainState(value);
    for (const listener of listeners) deliver(listener, state());
    return state();
  }

  function publishSync(value) {
    currentSyncState = freezeSyncState(value);
    for (const listener of syncListeners) deliver(listener, currentSyncState);
    return currentSyncState;
  }

  function beginRequest({ loading = false } = {}) {
    requestId += 1;
    requestController?.abort();
    requestController = new AbortController();
    if (loading) {
      authority = "unknown";
      accountProjection = null;
      accountId = null;
      publishSync({ kind: "loading" });
      publish({ kind: "loading" });
    }
    return Object.freeze({ id: requestId, controller: requestController });
  }

  function isCurrent(request) {
    return !disposed
      && request.id === requestId
      && !request.controller.signal.aborted;
  }

  function activateLocal(kind) {
    authority = "local";
    accountProjection = null;
    accountId = null;
    publishSync({ kind });
    return publish(localRepository.load());
  }

  function unavailable(reason) {
    authority = "unknown";
    accountProjection = null;
    accountId = null;
    publishSync({ kind: "unavailable", reason });
    return publish({ kind: "unavailable", operation: "account", reason });
  }

  function commandUnavailable(operation, reason) {
    return publish({ kind: "unavailable", operation, reason });
  }

  function applyProjection(projection, nextAccountId) {
    if (projection.kind === "disabled") return activateLocal("disabled");
    authority = "remote";
    accountProjection = projection;
    accountId = nextAccountId;
    publishSync(projection);
    return publish(domainStateFromAccount(projection));
  }

  function applyConflict(projection, nextAccountId, expectedRevision) {
    authority = projection.kind === "enabled" ? "remote" : "local";
    accountProjection = projection.kind === "enabled" ? projection : null;
    accountId = projection.kind === "enabled" ? nextAccountId : null;
    publishSync({ kind: "conflict", authoritativeState: projection });
    if (projection.kind === "disabled") return publish(localRepository.load());
    return publish(domainConflict(projection, expectedRevision));
  }

  async function freshSession(request, { refresh = false } = {}) {
    sessionLoadDepth += 1;
    try {
      const session = await sessionRepository[refresh ? "refresh" : "load"]();
      return isCurrent(request) ? session : null;
    } finally {
      sessionLoadDepth -= 1;
    }
  }

  async function reconcileUnauthorized(request) {
    const session = await freshSession(request, { refresh: true });
    if (!session || !isCurrent(request)) return state();
    if (session.kind === "signedOut") return activateLocal("signedOut");
    if (session.kind === "unavailable") {
      return unavailable(session.reason || "session-request-failed");
    }
    if (session.kind !== "ready") return unavailable("session-not-ready");
    return unavailable("account-authorization-rejected");
  }

  function handleSession(session) {
    if (session?.kind === "signedOut") return activateLocal("signedOut");
    if (session?.kind === "unavailable") {
      return unavailable(session.reason || "session-request-failed");
    }
    if (session?.kind !== "ready") return publish({ kind: "loading" });
    return null;
  }

  async function readRemote(request, session) {
    try {
      const projection = await remote.load({ signal: request.controller.signal });
      if (!isCurrent(request)) return state();
      if (projection.kind === "signedOut") return await reconcileUnauthorized(request);
      return applyProjection(projection, session.accountId);
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable("tutorial-request-failed");
    }
  }

  async function load() {
    const request = beginRequest({ loading: true });
    const session = await freshSession(request);
    if (!session || !isCurrent(request)) return state();
    if (handleSession(session) || session.kind !== "ready") return state();
    return readRemote(request, session);
  }

  async function mutateRemote(operation, command, expectedPublicRevision) {
    const request = beginRequest();
    try {
      const session = await freshSession(request, { refresh: true });
      if (!session || !isCurrent(request)) return state();
      if (session.kind !== "ready") {
        handleSession(session);
        return commandUnavailable(operation, "account-authority-changed");
      }
      if (accountId !== session.accountId) {
        await readRemote(request, session);
        return isCurrent(request)
          ? commandUnavailable(operation, "account-session-changed")
          : state();
      }
      const acknowledgement = await command(session, request.controller.signal);
      if (!acknowledgement || !isCurrent(request)) return state();
      if (acknowledgement.kind === "signedOut") {
        await reconcileUnauthorized(request);
        return commandUnavailable(operation, "account-authority-changed");
      }
      if (acknowledgement.kind === "conflict") {
        const result = applyConflict(
          acknowledgement.authoritativeState,
          session.accountId,
          expectedPublicRevision,
        );
        if (acknowledgement.authoritativeState.kind === "disabled") {
          return publish({
            kind: "conflict",
            expectedRevision: expectedPublicRevision,
            actualRevision: null,
            completedIds: [],
          });
        }
        return result;
      }
      const result = applyProjection(acknowledgement.authoritativeState, session.accountId);
      if (acknowledgement.authoritativeState.kind === "disabled") {
        return commandUnavailable(operation, "account-sync-disabled");
      }
      return result;
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable("tutorial-mutation-failed");
    }
  }

  async function complete(ids, options = {}) {
    const completedIds = canonicalizeTutorialProgressCommandIds(ids);
    const expectedRevision = validateOptions(options);
    if (authority === "local") return localRepository.complete(ids, options);
    if (authority !== "remote" || accountProjection?.kind !== "enabled") {
      return state();
    }
    const revision = accountProjection.revision;
    const publicRevision = accountProjection.state.kind === "ready" ? revision : null;
    if (expectedRevision !== undefined && expectedRevision !== publicRevision) {
      return publish(domainConflict(accountProjection, expectedRevision));
    }
    return mutateRemote(
      "complete",
      (session, signal) => remote.complete(session, revision, completedIds, { signal }),
      expectedRevision === undefined ? publicRevision : expectedRevision,
    );
  }

  async function reset(options = {}) {
    const expectedRevision = validateOptions(options);
    if (authority === "local") return localRepository.reset(options);
    if (authority !== "remote" || accountProjection?.kind !== "enabled") {
      return state();
    }
    const revision = accountProjection.revision;
    const publicRevision = accountProjection.state.kind === "ready" ? revision : null;
    if (expectedRevision !== undefined && expectedRevision !== publicRevision) {
      return publish(domainConflict(accountProjection, expectedRevision));
    }
    return mutateRemote(
      "reset",
      (session, signal) => remote.reset(session, revision, { signal }),
      expectedRevision === undefined ? publicRevision : expectedRevision,
    );
  }

  async function enableSync(initial) {
    const decodedInitial = decodeTutorialProgressEnabledState(initial);
    const request = beginRequest({ loading: true });
    try {
      const session = await freshSession(request, { refresh: true });
      if (!session || !isCurrent(request)) return syncState();
      if (session.kind !== "ready") {
        handleSession(session);
        return syncState();
      }
      const acknowledgement = await remote.enable(session, decodedInitial, {
        signal: request.controller.signal,
      });
      if (!acknowledgement || !isCurrent(request)) return syncState();
      if (acknowledgement.kind === "signedOut") {
        await reconcileUnauthorized(request);
      } else if (acknowledgement.kind === "conflict") {
        applyConflict(acknowledgement.authoritativeState, session.accountId, null);
      } else if (acknowledgement.authoritativeState.kind !== "enabled") {
        unavailable("tutorial-acknowledgement-invalid");
      } else {
        applyProjection(acknowledgement.authoritativeState, session.accountId);
      }
      return syncState();
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return syncState();
      unavailable("tutorial-mutation-failed");
      return syncState();
    }
  }

  async function exportRaw() {
    if (authority === "local") return localRepository.exportRaw();
    return freezeDomainState({
      kind: "unavailable",
      operation: "export",
      reason: "account-authority-has-no-local-raw-record",
    });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Repository listener must be a function.");
    }
    listeners.add(listener);
    deliver(listener, state());
    return () => listeners.delete(listener);
  }

  function subscribeSync(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Sync-state listener must be a function.");
    }
    syncListeners.add(listener);
    deliver(listener, currentSyncState);
    return () => syncListeners.delete(listener);
  }

  const unsubscribeLocal = localRepository.subscribe((nextState) => {
    if (!disposed && authority === "local") publish(nextState);
  });
  const unsubscribeSession = sessionRepository.subscribe((session) => {
    if (disposed || sessionLoadDepth > 0) return;
    if (session.kind === "loading" || session.kind === "uninitialized") {
      beginRequest({ loading: true });
    } else if (session.kind === "signedOut") {
      beginRequest();
      activateLocal("signedOut");
    } else if (session.kind === "unavailable") {
      beginRequest();
      unavailable(session.reason || "session-request-failed");
    } else if (session.kind === "ready") {
      const request = beginRequest({ loading: true });
      void readRemote(request, session);
    }
  });

  function dispose() {
    if (disposed) return;
    disposed = true;
    requestId += 1;
    requestController?.abort();
    listeners.clear();
    syncListeners.clear();
    unsubscribeLocal();
    unsubscribeSession();
  }

  return Object.freeze({
    exportRaw,
    load,
    state,
    complete,
    reset,
    subscribe,
    syncState,
    subscribeSync,
    enableSync,
    dispose,
  });
}

let browserOwner = null;
let browserRepository = null;
let browserWindow = null;

function browserView(repository) {
  return Object.freeze({
    [BROWSER_REPOSITORY_BRAND]: CONTRACT_VERSION,
    exportRaw: repository.exportRaw,
    load: repository.load,
    state: repository.state,
    complete: repository.complete,
    reset: repository.reset,
    subscribe: repository.subscribe,
    syncState: repository.syncState,
    subscribeSync: repository.subscribeSync,
    enableSync: repository.enableSync,
  });
}

export function browserTutorialProgressRepository() {
  const windowRef = globalThis.window;
  if (!windowRef || typeof windowRef !== "object") {
    throw new TypeError("The browser tutorial repository requires a window.");
  }
  if (browserWindow && browserWindow !== windowRef) {
    browserOwner?.dispose();
    browserOwner = null;
    browserRepository = null;
  }
  const shared = windowRef[BROWSER_REPOSITORY_PROPERTY];
  if (shared !== undefined) {
    if (
      shared[BROWSER_REPOSITORY_BRAND] !== CONTRACT_VERSION
      || !hasMethods(shared, [
        "exportRaw",
        "load",
        "state",
        "complete",
        "reset",
        "subscribe",
        "syncState",
        "subscribeSync",
        "enableSync",
      ])
    ) {
      throw new TypeError("The shared tutorial progress repository is incompatible.");
    }
    browserRepository = shared;
  }
  if (!browserRepository) {
    browserOwner = createTutorialProgressSyncRepository({
      localRepository: browserLocalTutorialProgressRepository(),
      sessionRepository: browserAccountStateSessionRepository(),
      apiBaseUrl: windowRef.__fishystuffApiBaseUrl,
    });
    browserRepository = browserView(browserOwner);
  }
  browserWindow = windowRef;
  windowRef[BROWSER_REPOSITORY_PROPERTY] = browserRepository;
  return browserRepository;
}
